import React, { useState, useEffect } from 'react';
import { supabaseClient } from '../utils/supabaseClient';
import { Mail, Lock, LogIn, UserPlus, KeyRound, ArrowLeft } from 'lucide-react';
import RegisterPage from './RegisterPage';
import { Helmet } from 'react-helmet';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isRecoveryMode, setIsRecoveryMode] = useState(false);

  useEffect(() => {
    document.title = 'ログイン | RepliGo（レプリゴ）';
  }, []);

  // パスワードリセットのハッシュパラメータをチェック
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');

    if (type === 'recovery') {
      setIsRecoveryMode(true);
      setShowResetPassword(true);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim() || !password.trim()) {
      setErrorMessage("メールアドレスとパスワードを入力してください");
      return;
    }
    
    setLoading(true);
    setErrorMessage('');
    
    try {
      // 通常のログイン処理
      const { error } = await supabaseClient.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        let message = error.message;
        if (error.message.includes("Invalid login credentials")) {
          message = "ログイン情報が正しくありません";
        }
        setErrorMessage(message);
      }
    } catch (error) {
      setErrorMessage("ログイン処理中にエラーが発生しました");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!resetEmail.trim()) {
      setResetMessage("メールアドレスを入力してください");
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const { error } = await supabaseClient.auth.resetPasswordForEmail(resetEmail, {
        redirectTo: `${window.location.origin}/app?mode=reset`
      });

      if (error) {
        setResetMessage(error.message);
      } else {
        setResetMessage("パスワードリセット用のメールを送信しました。メールをご確認ください。");
        setResetEmail('');
      }
    } catch (error) {
      setResetMessage("パスワードリセット処理中にエラーが発生しました");
      console.error(error);
    } finally {
      setResetLoading(false);
    }
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newPassword.trim() || !confirmNewPassword.trim()) {
      setResetMessage("新しいパスワードを入力してください");
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setResetMessage("パスワードが一致しません");
      return;
    }

    setResetLoading(true);
    setResetMessage('');

    try {
      const { error } = await supabaseClient.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setResetMessage(error.message);
      } else {
        setResetMessage("パスワードを変更しました。ログイン画面に戻ります。");
        setTimeout(() => {
          window.location.href = '/app';
        }, 2000);
      }
    } catch (error) {
      setResetMessage("パスワード変更処理中にエラーが発生しました");
      console.error(error);
    } finally {
      setResetLoading(false);
    }
  };

  if (showRegister) {
    return <RegisterPage onBack={() => setShowRegister(false)} />;
  }

  if (showResetPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="bg-[#007AFF] p-6 text-center">
              <img src="/img/logo-white.png" alt="RepliGo（レプリゴ）" className="h-12 mx-auto" />
            </div>
            
            {isRecoveryMode ? (
              <form onSubmit={handleUpdatePassword} className="p-8 space-y-6">
                <div className="space-y-2">
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    新しいパスワード
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="newPassword"
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="confirmNewPassword" className="block text-sm font-medium text-gray-700">
                    新しいパスワード（確認）
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmNewPassword"
                      type="password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                    />
                  </div>
                </div>
                
                {resetMessage && (
                  <div className={`text-sm font-medium ${resetMessage.includes('変更しました') ? 'text-green-600' : 'text-red-600'}`}>
                    {resetMessage}
                  </div>
                )}
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => {
                      setIsRecoveryMode(false);
                      setShowResetPassword(false);
                    }}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    戻る
                  </button>
                  
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        処理中...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <KeyRound className="mr-2 h-4 w-4" />
                        パスワードを変更
                      </span>
                    )}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="p-8 space-y-6">
                <div className="space-y-2">
                  <label htmlFor="resetEmail" className="block text-sm font-medium text-gray-700">
                    メールアドレス
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="resetEmail"
                      type="email"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                      placeholder="example@example.com"
                    />
                  </div>
                </div>
                
                {resetMessage && (
                  <div className={`text-sm font-medium ${resetMessage.includes('送信しました') ? 'text-green-600' : 'text-red-600'}`}>
                    {resetMessage}
                  </div>
                )}
                
                <div className="flex space-x-4">
                  <button
                    type="button"
                    onClick={() => setShowResetPassword(false)}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    戻る
                  </button>
                  
                  <button
                    type="submit"
                    disabled={resetLoading}
                    className="flex-1 flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {resetLoading ? (
                      <span className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        処理中...
                      </span>
                    ) : (
                      <span className="flex items-center">
                        <KeyRound className="mr-2 h-4 w-4" />
                        リセットメール送信
                      </span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>ログイン | RepliGo（レプリゴ）</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="RepliGo（レプリゴ）のログインページです。アカウントをお持ちの方はこちらからログインしてください。" />
        <link rel="canonical" href="https://repligo.forceai.jp/login" />
      </Helmet>
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-blue-100 p-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl shadow-xl overflow-hidden">
            <div className="bg-[#007AFF] p-6 text-center">
              <img src="/img/logo-white.png" alt="RepliGo（レプリゴ）" className="h-12 mx-auto" />
            </div>
            
            <form onSubmit={handleLogin} className="p-8 space-y-6">
              <div className="space-y-2">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  メールアドレス
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                    placeholder="example@example.com"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  パスワード
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#007AFF] focus:border-[#007AFF]"
                  />
                </div>
              </div>
              
              {errorMessage && (
                <div className="text-red-600 text-sm font-medium">
                  {errorMessage}
                </div>
              )}
              
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#007AFF] hover:bg-[#0066CC] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    処理中...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <LogIn className="mr-2 h-4 w-4" />
                    ログイン
                  </span>
                )}
              </button>

              <div className="flex flex-col space-y-3">
                <button
                  type="button"
                  onClick={() => setShowResetPassword(true)}
                  className="w-full flex justify-center items-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#007AFF]"
                >
                  <KeyRound className="mr-2 h-4 w-4" />
                  パスワードを忘れた場合
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default LoginPage;
